import { useState, useEffect } from 'react';

interface PWAInstallState {
  isInstallable: boolean;
  isInstalled: boolean;
  deferredPrompt: any;
  showPrompt: boolean;
}

const INSTALL_PROMPT_STORAGE_KEY = 'pwa_install_prompt_last_shown';
const INSTALL_CHECK_STORAGE_KEY = 'pwa_app_installed';

export const usePWAInstall = () => {
  const [state, setState] = useState<PWAInstallState>({
    isInstallable: false,
    isInstalled: false,
    deferredPrompt: null,
    showPrompt: false,
  });

  useEffect(() => {
    console.log('PWA Install: Hook initialized');
    console.log('PWA Install: beforeinstallprompt supported:', 'onbeforeinstallprompt' in window);
    console.log('PWA Install: Service worker supported:', 'serviceWorker' in navigator);
    console.log('PWA Install: Current URL:', window.location.href);
    console.log('PWA Install: Is HTTPS:', window.location.protocol === 'https:' || window.location.hostname === 'localhost');

    // Check if app is already installed
    const checkIfInstalled = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
      const isInWebAppiOS = (window.navigator as any).standalone === true;
      const isInstalled = localStorage.getItem(INSTALL_CHECK_STORAGE_KEY) === 'true';

      if (isStandalone || isInWebAppiOS || isInstalled) {
        setState(prev => ({ ...prev, isInstalled: true }));
        return true;
      }
      return false;
    };

    // Check if we should show the prompt (every 2 days)
    const shouldShowPrompt = () => {
      const lastShown = localStorage.getItem(INSTALL_PROMPT_STORAGE_KEY);
      if (!lastShown) return true;

      const lastShownDate = new Date(lastShown);
      const twoDaysAgo = new Date();
      twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);

      return lastShownDate < twoDaysAgo;
    };

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      console.log('PWA Install: beforeinstallprompt event fired!');
      e.preventDefault();
      const prompt = (e as any);

      const isInstalled = checkIfInstalled();
      const shouldShow = shouldShowPrompt();
      
      console.log('PWA Install: App already installed:', isInstalled);
      console.log('PWA Install: Should show prompt:', shouldShow);

      if (!isInstalled && shouldShow) {
        console.log('PWA Install: Setting up prompt to show');
        setState(prev => ({
          ...prev,
          isInstallable: true,
          deferredPrompt: prompt,
          showPrompt: true,
        }));
      } else {
        console.log('PWA Install: Not showing prompt - storing for later use');
        setState(prev => ({
          ...prev,
          isInstallable: true,
          deferredPrompt: prompt,
        }));
      }
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      setState(prev => ({ ...prev, isInstalled: true, showPrompt: false }));
      localStorage.setItem(INSTALL_CHECK_STORAGE_KEY, 'true');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Initial check
    if (!checkIfInstalled() && shouldShowPrompt()) {
      // Show the install prompt even without beforeinstallprompt event
      // Many browsers don't fire this event consistently
      setTimeout(() => {
        console.log('PWA Install: Timeout reached - checking state');
        console.log('PWA Install: Current state:', state);
        setState(prev => {
          if (!prev.isInstalled) {
            console.log('PWA Install: Showing install prompt after timeout');
            return { ...prev, isInstallable: true, showPrompt: true };
          }
          return prev;
        });
      }, 2000);
    }

    // More aggressive approach to trigger beforeinstallprompt
    const triggerInstallabilityCheck = () => {
      console.log('PWA Install: Triggering installability check');
      
      // Simulate user engagement events that might trigger beforeinstallprompt
      const engagementEvents = ['click', 'scroll', 'keydown', 'touchstart', 'mousedown'];
      
      engagementEvents.forEach(eventType => {
        document.addEventListener(eventType, () => {
          console.log(`PWA Install: User engagement detected: ${eventType}`);
          
          // Check again after user interaction
          setTimeout(() => {
            if (!state.deferredPrompt) {
              console.log('PWA Install: Still no deferred prompt after user engagement');
              
              // Try to force a navigation event that might trigger the prompt
              const currentUrl = window.location.href;
              window.history.replaceState({}, '', currentUrl);
            }
          }, 500);
        }, { once: true });
      });
    };
    
    // Run the installability check after a delay
    setTimeout(() => {
      console.log('PWA Install: Checking if beforeinstallprompt fired after 3 seconds');
      console.log('PWA Install: Deferred prompt available:', !!state.deferredPrompt);
      
      if (!state.deferredPrompt) {
        console.log('PWA Install: No beforeinstallprompt event fired - setting up engagement triggers');
        triggerInstallabilityCheck();
      }
    }, 3000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []); // Empty dependency array is intentional - we only want this to run once on mount

  // Function to check PWA installation criteria
  const checkPWAInstallability = () => {
    const criteria = {
      https: window.location.protocol === 'https:' || window.location.hostname === 'localhost',
      serviceWorker: 'serviceWorker' in navigator,
      manifest: document.querySelector('link[rel="manifest"]') !== null,
      beforeinstallprompt: 'onbeforeinstallprompt' in window,
      userAgent: navigator.userAgent.toLowerCase(),
      isStandalone: window.matchMedia('(display-mode: standalone)').matches,
      isInWebAppiOS: (window.navigator as any).standalone === true,
    };

    console.log('PWA Install: Installation criteria check:', criteria);
    
    // Check if app is already installed
    if (criteria.isStandalone || criteria.isInWebAppiOS) {
      console.log('PWA Install: App is already installed');
      return { installable: false, reason: 'already_installed' };
    }

    // Check basic requirements
    if (!criteria.https) {
      console.log('PWA Install: Not HTTPS - installation not possible');
      return { installable: false, reason: 'not_https' };
    }

    if (!criteria.serviceWorker) {
      console.log('PWA Install: No service worker support - installation not possible');
      return { installable: false, reason: 'no_service_worker' };
    }

    if (!criteria.manifest) {
      console.log('PWA Install: No manifest found - installation not possible');
      return { installable: false, reason: 'no_manifest' };
    }

    // Check if beforeinstallprompt is supported
    if (!criteria.beforeinstallprompt) {
      console.log('PWA Install: beforeinstallprompt not supported - manual installation required');
      return { installable: false, reason: 'no_beforeinstallprompt' };
    }

    // Check if we have a deferred prompt (but don't fail if we don't)
    if (!state.deferredPrompt) {
      console.log('PWA Install: No deferred prompt available - will show manual instructions');
      return { installable: false, reason: 'no_deferred_prompt' };
    }

    console.log('PWA Install: All criteria met - installation should be possible');
    return { installable: true, reason: 'all_criteria_met' };
  };

  const installApp = async () => {
    console.log('PWA Install: installApp called');
    
    // First, try to use the deferred prompt if available
    if (state.deferredPrompt) {
      try {
        console.log('PWA Install: Using deferred prompt');
        state.deferredPrompt.prompt();
        
        const { outcome } = await state.deferredPrompt.userChoice;
        console.log('PWA Install: User choice outcome:', outcome);

        if (outcome === 'accepted') {
          console.log('PWA Install: User accepted installation');
          setState(prev => ({ ...prev, isInstalled: true, showPrompt: false }));
          localStorage.setItem(INSTALL_CHECK_STORAGE_KEY, 'true');
          return true;
        } else {
          console.log('PWA Install: User dismissed installation');
          setState(prev => ({ ...prev, showPrompt: false }));
          return false;
        }
      } catch (error) {
        console.error('PWA Install: Error during app installation:', error);
        setState(prev => ({ ...prev, showPrompt: false }));
        return false;
      }
    }
    
    // No deferred prompt available — browser hasn't offered install yet or
    // doesn't support it (e.g. iOS Safari, Firefox). Return false so the
    // caller can show manual "Add to Home Screen" instructions instead.
    console.log('PWA Install: No deferred prompt — showing manual instructions');
    setState(prev => ({ ...prev, showPrompt: false }));
    return false;
  };

  const dismissPrompt = () => {
    setState(prev => ({ ...prev, showPrompt: false }));
    localStorage.setItem(INSTALL_PROMPT_STORAGE_KEY, new Date().toISOString());
  };

  const resetPrompt = () => {
    localStorage.removeItem(INSTALL_PROMPT_STORAGE_KEY);
    localStorage.removeItem(INSTALL_CHECK_STORAGE_KEY);
    setState(prev => ({
      ...prev,
      isInstalled: false,
      showPrompt: false,
      isInstallable: true
    }));
  };



  return {
    ...state,
    installApp,
    dismissPrompt,
    resetPrompt,
  };
};
